/**
 * 작성자 : 한영광
 * 날짜 : 2025.05.27
 * 기능 : (사내)업무관리-결재관리-기안관리,결재내역
 */

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { AttendanceD, IDraft } from 'customTypes';
import { userState } from '../atoms';
import { today } from '../utils/formatDate';
import { isAwaitingApprovalFrom, renderApprovalStatus } from '../utils/renderApprovalStatus';
import List from '../pages/c_work/work4/list';
import Form from '../pages/c_work/work4/form';
import { ContentsContainerRoot } from '../assets/styles/ContentsContainerRoot';
import GetFetchStatus from '../hooks/GetFetchStatus';
import BackButton from './BackButton';
import { COMCD_A_STATUS, COMCD_ATTENDANCE_GUBUN } from '../_constants';
import { useFetchCommonCodeList } from '../services/useSetCodeListInSelectBoxForm';
import useBackHistory from '../hooks/useBackHistory';

const Root = styled(ContentsContainerRoot)`
  &.hideRoot {
    visibility: hidden;
    position: absolute;
  }

  .formTitle {
    padding: 0 0.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--text-primary);
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    .table {
      height: fit-content;
    }
  }
`;

const INIT_ROW = {
  wDate: '',
  eDate: '',
  cCd: '',
  caCd: '',
  status: '0',
  name: '',
  department: '',
  delYn: '',
  writer: '',
  editor: '',
  cdraftD1: [],
  cdraftD2: [],
  date: '',
  title: '',
  content: '',
  note: '',
  currentUserId: '',
  tempSaveYn: 'N' as 'Y' | 'N',
};

// 날짜 포맷 변환 함수
const formatDateWithDay = (dateString: string) => {
  const date = dayjs(dateString);
  return date.format('YYYY-MM-DD (ddd)');
};

// 날짜+시간에서 시간만 추출하여 변환 (2024-11-22 10:30 -> 10:30)
const formatDateTime = (dateTimeString: string) => {
  const timeStr = dateTimeString?.split(' ')[1];
  return timeStr || '';
};

const DraftForm = ({ approvalType }: { approvalType: 'request' | 'approval' }) => {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);
  const size = useOutletContext<any>();
  const [viewTable, setViewTable] = useState<boolean>(true);
  const [rowState, setRowState] = useState<IDraft>(INIT_ROW);
  const [isNewAdd, setIsNewAdd] = useState(true);
  const [tableState, setTableState] = useState<IDraft[] | null>(null);
  const [initTableState, setInitTableState] = useState<IDraft[] | null>(null);
  const { data: cUserList } = GetFetchStatus({ path: '/user/company', req: { hCd: userInfo.hCd }, useLoginUserInfo: false });
  const { data: caStatusComCdList } = useFetchCommonCodeList(COMCD_A_STATUS, true);
  const { data: caGubunComCdList } = useFetchCommonCodeList(COMCD_ATTENDANCE_GUBUN, true);
  const dataKey = { name: 'name', department: 'department' };

  const formatInTuiForm = (aList: any[]) => {
    // 공통코드 로딩 전에는 빈 배열로 덮어쓰지 않도록 호출측에서 가드 (근태 AttendanceForm과 동일)
    if (!caStatusComCdList || !caGubunComCdList) {
      return [];
    }

    const tuiForm = aList.map((el: any) => {
      // 결재 탭에서 내가 지금 처리해야 하는 건인지. 보류처럼 내가 이미 처리한 건은 실제 상태를 보여준다
      const isAwaitingMyApproval =
        approvalType === 'approval' &&
        isAwaitingApprovalFrom(
          userInfo.userId,
          el.currentUserId,
          el.cdraftD1?.map((line: { userId: string; lstatus: string }) => ({ userId: line.userId, caLstatus: line.lstatus }))
        );

      return {
        ...el,
        isAwaitingMyApproval,
        ...{ appType: { name1: el[dataKey.name], name2: el[dataKey.department] } },
        period: { name1: `${formatDateWithDay(el.sDate)} ~ ${formatDateWithDay(el.fDate)}`, name2: `${formatDateTime(el.sDate)} ~ ${formatDateTime(el.fDate)}` },
        appLine: el.attendanceD1?.map((d1: AttendanceD) => d1.userName).join(', '),
        caGubunStatus: {
          name1: caGubunComCdList?.find((cd) => cd[COMCD_ATTENDANCE_GUBUN] === el.caGubun)?.cdName ?? '',
          name2: approvalType === 'approval' && el.currentUserId === userInfo.userId ? t('결재 대기') : caStatusComCdList?.find((cd) => cd[COMCD_A_STATUS] === el.caStatus)?.cdName ?? '',
          color1: approvalType === 'approval' && el.currentUserId === userInfo.userId ? 'violet' : renderApprovalStatus(el.caStatus || '0').color,
        },
        lStatus: {
          name2: isAwaitingMyApproval
            ? t('결재 대기')
            : approvalType === 'request' && el.tempSaveYn === 'Y'
            ? t('임시')
            : caStatusComCdList?.find((cd) => cd[COMCD_A_STATUS] === el.status)?.cdName ?? '',
          color1: isAwaitingMyApproval ? 'violet' : renderApprovalStatus(el.status).color,
        },
      };
    });
    return tuiForm;
  };

  // 기안 신청일 때
  const setUserBasicInfo = async () => {
    if (cUserList) {
      const u = cUserList.find((el: any) => el.userId === userInfo.userId);

      if (u) {
        // 로그인한 유저 기본정보
        setRowState({
          ...INIT_ROW,
          [dataKey.name]: u.userName,
          [dataKey.department]: u.partName,
          wDate: today(),
          date: today(),
          tempSaveYn: 'N',
        });
      }
    }
  };

  useEffect(() => {
    setRowState(INIT_ROW);
    setIsNewAdd(true);
    setViewTable(true);
    if (approvalType === 'request') {
      setUserBasicInfo();
    }
  }, [cUserList, approvalType]);

  // 탭(신청/결재) 전환 시에만 목록 초기화 — cUserList 재조회와 분리
  useEffect(() => {
    setTableState(null);
    setInitTableState(null);
  }, [approvalType]);

  useEffect(() => {
    if (!isNewAdd) setViewTable(false);
  }, [isNewAdd]);

  const addBtnAction = () => {
    setIsNewAdd(true);
    setUserBasicInfo();
    if (size.innerSize.W <= 1024) setViewTable(false);
  };

  const backToMain = () => {
    setIsNewAdd(true);
    setViewTable(true);
  };

  const { onBackPrev } = useBackHistory({ isOpen: !viewTable, onPrev: backToMain, pageName: 'draftForm' });

  const formTypeName = t('기안');

  return (
    <div className={`content-container ${size.innerSize.W > 1024 ? 'twoColumn column-55 max800' : 'oneColumn'}`}>
      <Root loading={false} className={size.innerSize.W > 1024 || viewTable ? 'showRoot' : 'hideRoot'}>
        <List
          approvalType={approvalType}
          tableState={tableState}
          setTableState={setTableState}
          initTableState={initTableState}
          setInitTableState={setInitTableState}
          setRowState={setRowState}
          isNewAdd={isNewAdd}
          setIsNewAdd={setIsNewAdd}
          addBtnAction={addBtnAction}
          formatInTuiForm={formatInTuiForm}
        />
      </Root>
      {(size.innerSize.W > 1024 || !viewTable) && (
        <Root loading={false}>
          <div className='inputFormsWrapper flexRowEm'>
            {size.innerSize.W <= 1024 && <BackButton func={onBackPrev} />}
            <div className='formTitle'>{isNewAdd ? t(`${formTypeName} 신청`) : t('신청 정보')}</div>
          </div>
          <Form
            approvalType={approvalType}
            rowState={rowState}
            setRowState={setRowState}
            isNewAdd={isNewAdd}
            setIsNewAdd={setIsNewAdd}
            tableState={tableState}
            setInitTableState={setInitTableState}
            formatInTuiForm={formatInTuiForm}
          />
        </Root>
      )}
    </div>
  );
};

export default DraftForm;
